import { useState, useEffect } from 'react';
import { Card, Typography, Timeline, Tag, Divider, Result, Spin } from 'antd';
import { RocketOutlined, CheckCircleOutlined, BugOutlined, SecurityScanOutlined } from '@ant-design/icons';
import { releaseNotesDataSE, releaseNotesDataEG } from '../../utils/commonStringsConsts';
import { useSelector } from 'react-redux';
import { Region } from '../../utils/commonInterfaces';
import { getRegion } from '../regionWrapper';

const { Title, Text, Paragraph } = Typography;

const getIconByType = (type: string) => {
    switch (type) {
        case 'feature':
            return <RocketOutlined />;
        case 'fix':
            return <BugOutlined />;
        case 'security':
            return <SecurityScanOutlined />;
        default:
            return <CheckCircleOutlined />;
    }
};

const getTagColor = (type: string) => {
    switch (type) {
        case 'feature':
            return 'green';
        case 'fix':
            return 'orange';
        case 'security':
            return 'red';
        default:
            return 'blue';
    }
};

/**
 * Version Type always blue
 * 
 * How Semantic Versioning works:
 * - MAJOR (x.0.0): Breaking changes → increments MAJOR, resets MINOR and PATCH to 0
 *   Example: 1.9.17 → 2.0.0
 * 
 * - MINOR (x.y.0): New features → increments MINOR, resets PATCH to 0
 *   Example: 1.9.17 → 1.10.0 (not 1.9.18)
 * 
 * - PATCH (x.y.z): Bug fixes → increments PATCH only
 *   Example: 1.9.17 → 1.9.18
 */
const getVersionTypeColor = (versionType: string) => {
    // Always return blue for version type
    return 'blue';
};

export const ReleaseNotes = () => {
    const region: Region = getRegion() ?? Region.EG;
    const [isInternalEmployee] = useState(true); // TODO: SE Check user role from Redux/API
    const isHR = useSelector((state: any) => state?.auth?.isHR);
    const releaseNotesData = region === Region.SE ? releaseNotesDataSE : releaseNotesDataEG;

    // SE Check if user is internal employee (not HR)
    useEffect(() => {
        // TODO: SE Implement actual check from Redux state or API
        // const userRole = useSelector((state: any) => state.auth.userRole);
        // setIsInternalEmployee(userRole !== 'HR');

        // Set body background to gray
        document.body.style.backgroundColor = '#f8f9fa';

        return () => {
            // Reset body background when component unmounts
            document.body.style.backgroundColor = '';
        };
    }, []);

    // EG Still loading permissions
    if (region === Region.EG && (isHR === null || typeof isHR === 'undefined')) {
        return (
            <div className="release-notes-container">
                <div style={{ padding: 50 }}>
                    <Spin size="large" tip="Loading..." />
                </div>
            </div>
        );
    }

    // EG HR should not access Release Notes
    if (region === Region.EG && isHR === true) {
        return (
            <div className="release-notes-container">
                <Result
                    status="403"
                    title="Permission"
                    subTitle="You don't have permission to access this page"
                />
            </div>
        );
    }

    if (region === Region.SE && !isInternalEmployee) {
        return (
            <div className="release-notes-container">
                <Result
                    status="403"
                    title="Access Restricted"
                    subTitle="This page is only accessible to internal NowPay employees."
                />
            </div>
        );
    }

    return (
        <div className="release-notes-container">
            <div className="release-notes-wrapper">
                {/* Header Section */}
                <div className="release-notes-header">
                    <div className="header-content">
                        <Title level={1} className="main-title">
                            What's New?
                        </Title>
                        <Text className="subtitle">
                            Stay updated with the latest features and improvements
                        </Text>
                    </div>
                    <div className="header-decoration">
                        <RocketOutlined className="rocket-icon" />
                    </div>
                </div>

                {/* Release Notes Timeline */}
                <div className="release-notes-content">
                    <Timeline
                        mode="left"
                        className="release-timeline"
                    >
                        {releaseNotesData?.map((release, index) => (
                            <Timeline.Item
                                key={release.version}
                                dot={
                                    <div className={`timeline-dot timeline-dot-${release.type}`}>
                                        {getIconByType(release.type)}
                                    </div>
                                }
                            >
                                <Card
                                    className={`release-card release-card-${release.type} ${index === 0 ? 'latest-release' : ''}`}
                                    hoverable
                                >
                                    <div className="release-card-header">
                                        <div className="version-section">
                                            <Title level={2} className="version-title">
                                                Version {release.version}
                                            </Title>
                                            <Tag color={getVersionTypeColor(release.versionType)} className="version-tag">
                                                {release.versionType}
                                            </Tag>
                                            <Tag color={getTagColor(release.type)} className="version-tag">
                                                {release.type.toUpperCase()}
                                            </Tag>
                                        </div>
                                        <Text className="release-date">{release.date}</Text>
                                    </div>

                                    <Divider className="release-divider" />

                                    <div className="release-notes-list">
                                        {release.notes.map((note, noteIndex) => (
                                            <div key={noteIndex} className="release-note-item">
                                                <CheckCircleOutlined className="note-icon" />
                                                <Paragraph className="note-text">{note}</Paragraph>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                </div>
            </div>
        </div>
    );
};
